import { createMemo, For, Show, type JSX } from 'solid-js';
import { flatten, useBoxStacks, useDestroyBox } from '../lib/queries';
import { pushToast } from '../lib/toast';
import type { BoxListItem } from '../lib/types';
import { Tag } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Dialog } from '../ui/Dialog';
import { ErrorAlert } from './states';

export function DestroyBoxDialog(props: {
  box: Pick<BoxListItem, 'box_id' | 'display' | 'box_status' | 'instance_id' | 'hostname'> | undefined;
  onClose: () => void;
  onDestroyed?: () => void;
}): JSX.Element {
  const destroy = useDestroyBox();
  const stacks = useBoxStacks(
    () => props.box?.box_id ?? '',
    () => props.box !== undefined && props.box.box_status === 'Ready',
  );
  const deployed = createMemo(() => flatten(stacks.data?.pages));

  async function confirm(): Promise<void> {
    const box = props.box;
    if (!box) return;
    try {
      await destroy.mutateAsync(box.box_id);
      pushToast('success', `Destroying box ${box.display}.`);
      props.onDestroyed?.();
      props.onClose();
    } catch {
      // Surfaced inside the dialog by the error alert below.
    }
  }

  return (
    <Dialog
      open={props.box !== undefined}
      onClose={props.onClose}
      title="Destroy this box?"
      description="The machine, its address and everything running on it are deleted. This cannot be undone."
      footer={
        <>
          <Button variant="secondary" onClick={props.onClose} disabled={destroy.isPending}>
            Cancel
          </Button>
          <Button variant="danger" loading={destroy.isPending} onClick={confirm}>
            Destroy box
          </Button>
        </>
      }
    >
      <Show when={props.box}>
        {(box) => (
          <div class="stack gap-4">
            <div class="kv">
              <span class="kv__k">Box</span>
              <span class="kv__v">{box().display}</span>
              <span class="kv__k">Box ID</span>
              <span class="kv__v kv__v--mono">{box().box_id}</span>
              <span class="kv__k">Hostname</span>
              <span class="kv__v kv__v--mono">{box().hostname ?? '—'}</span>
              <span class="kv__k">Instance</span>
              <span class="kv__v kv__v--mono">{box().instance_id ?? '—'}</span>
              <span class="kv__k">Stacks</span>
              <span class="kv__v">
                <Show when={deployed().length > 0} fallback={<span class="muted text-sm">None deployed</span>}>
                  <span class="row gap-2 wrap">
                    <For each={deployed()}>{(stack) => <Tag>{stack.display}</Tag>}</For>
                  </span>
                </Show>
              </span>
            </div>
            <Show when={destroy.isError}>
              <ErrorAlert title="Could not destroy the box" error={destroy.error} />
            </Show>
          </div>
        )}
      </Show>
    </Dialog>
  );
}
